import { supabase } from "@/integrations/supabase/client";

/**
 * R2 Upload — image compression policy
 * -------------------------------------
 * All image uploads go through `uploadImageToR2WithVariants` which applies:
 *   - Format conversion: WebP (universal browser support, ~30% smaller than JPEG)
 *   - Max dimension:     1920px (longest edge — preserves print quality)
 *   - Quality:           80 (visually lossless for photos and product shots)
 *   - Hard size cap:     1 MB per stored variant
 *
 * Do NOT bypass these defaults without team review — they keep R2 storage
 * costs predictable and CDN latency low. To audit and clean up orphaned
 * uploads (files in R2 with no matching DB row) use:
 *   /file-manager → "Find Orphans" tab.
 */

const IMAGE_MIME_TYPES = new Set([
  "image/png", "image/jpeg", "image/jpg", "image/webp", "image/gif", "image/bmp", "image/heic",
]);
const R2_DOMAIN = "pub-751830794bf1401f933c0ea71fb0430e.r2.dev";
const INTERNAL_MEDIA_SEGMENT = "/internal-media/";
const INTERNAL_MEDIA_FOLDER_PREFIXES = [
  "items",
  "purchases",
  "purchase-orders",
  "sales",
  "orders",
  "business-assets",
];

function isImageFile(file: File | Blob): boolean {
  if (file instanceof File) {
    return IMAGE_MIME_TYPES.has(file.type) || /\.(png|jpe?g|webp|gif|bmp|heic)$/i.test(file.name);
  }
  return IMAGE_MIME_TYPES.has(file.type);
}

async function ensureUploadSession(): Promise<void> {
  const { data: sessionData } = await supabase.auth.getSession();
  let token = sessionData?.session?.access_token ?? null;

  if (!token) {
    const { data: refreshed, error } = await supabase.auth.refreshSession();
    if (error) throw new Error("Session expired. Please refresh and sign in again.");
    token = refreshed.session?.access_token ?? null;
  }

  if (!token) throw new Error("Session expired. Please refresh and sign in again.");
}

async function refreshUploadSession(): Promise<void> {
  const { error, data } = await supabase.auth.refreshSession();
  if (error || !data.session?.access_token) {
    throw new Error("Session expired. Please refresh and sign in again.");
  }
}

function getInvokeErrorMessage(error: unknown, fallback: string): string {
  if (!error) return fallback;
  if (typeof error === "string") return error;
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === "object") {
    const maybeMessage = (error as { message?: string; context?: string; error?: string });
    if (maybeMessage.message) return maybeMessage.message;
    if (maybeMessage.error) return maybeMessage.error;
    if (maybeMessage.context) return maybeMessage.context;
  }
  return fallback;
}

export async function invokeR2Manage<T = any>(body: Record<string, any>): Promise<T> {
  await ensureUploadSession();

  let lastError: Error | null = null;

  for (let attempt = 0; attempt < 2; attempt++) {
    const { data, error } = await supabase.functions.invoke("r2-manage", { body });
    if (!error) {
      const result = data as { success?: boolean; error?: string } & T;
      if (result && typeof result === "object" && "success" in result && result.success === false) {
        throw new Error(result.error || "Operation failed");
      }
      return result as T;
    }

    lastError = new Error(getInvokeErrorMessage(error, "Operation failed"));
    if (attempt === 0) {
      await refreshUploadSession();
      continue;
    }
  }

  throw lastError ?? new Error("Operation failed");
}

export async function invokeVpsMediaManage<T = any>(body: Record<string, any>): Promise<T> {
  await ensureUploadSession();

  const { data, error } = await supabase.functions.invoke("vps-media", { body });
  if (error) {
    throw new Error(getInvokeErrorMessage(error, "VPS media operation failed"));
  }

  const result = data as { success?: boolean; error?: string } & T;
  if (result && typeof result === "object" && "success" in result && result.success === false) {
    throw new Error(result.error || "VPS media operation failed");
  }
  return result as T;
}

export interface MediaListFile {
  key: string;
  size: number;
  lastModified: string;
  url: string;
}

export async function listAllVpsMedia(prefix?: string): Promise<MediaListFile[]> {
  const response = await invokeVpsMediaManage<{ files?: MediaListFile[] }>({
    action: "list-all",
    ...(prefix ? { prefix } : {}),
  });
  return response.files ?? [];
}

function isInternalMediaFolder(folder: string): boolean {
  const normalizedFolder = folder.replace(/^\/+/, "").toLowerCase();
  return INTERNAL_MEDIA_FOLDER_PREFIXES.some((prefix) =>
    normalizedFolder === prefix || normalizedFolder.startsWith(`${prefix}/`)
  );
}

// Mirrors the canonical tenants.is_legacy flag used server-side by
// isLegacyTenant() in supabase/functions/_shared/tenantSecrets.ts. This
// client-side check only picks WHICH edge function to call (r2-upload vs
// vps-media) — it is NOT the security boundary. vps-media re-verifies
// isLegacyTenant() server-side against the caller's authenticated tenant_id
// and rejects any non-legacy tenant regardless of what the client requests.
//
// tenants.is_legacy has no UPDATE RLS policy for authenticated users (SELECT
// "own row only" + INSERT only), so unlike company_name it cannot be
// tenant-tampered — safe to trust for routing purposes.
//
// Cached per signed-in user for the life of the session — avoids a DB round
// trip on every upload. Keyed by user id so switching accounts (rare, but
// possible via impersonation/dev tools) doesn't leak a stale answer.
let cachedLegacyCheck: { userId: string; result: Promise<boolean> } | null = null;

async function isCurrentTenantLegacy(): Promise<boolean> {
  const { data: sessionData } = await supabase.auth.getSession();
  const userId = sessionData?.session?.user?.id;
  if (!userId) return false;

  if (cachedLegacyCheck?.userId === userId) {
    return cachedLegacyCheck.result;
  }

  const result = (async () => {
    const { data: profile, error: profileError } = await supabase
      .from("profiles")
      .select("tenant_id")
      .eq("id", userId)
      .maybeSingle();
    const tenantId = profile?.tenant_id;
    if (profileError || !tenantId) return false;

    const { data: tenant, error: tenantError } = await supabase
      .from("tenants" as any)
      .select("is_legacy")
      .eq("id", tenantId)
      .maybeSingle();
    if (tenantError || !tenant) return false;
    return (tenant as { is_legacy?: boolean }).is_legacy === true;
  })();

  cachedLegacyCheck = { userId, result };
  return result;
}

async function shouldUseInternalMedia(folder: string): Promise<boolean> {
  if (!isInternalMediaFolder(folder)) return false;
  return isCurrentTenantLegacy();
}

async function invokeMediaUpload(functionName: "r2-upload" | "vps-media", body: FormData): Promise<string> {
  await ensureUploadSession();
  const { data, error } = await supabase.functions.invoke(functionName, { body });
  if (error) {
    throw new Error(getInvokeErrorMessage(error, "Upload failed"));
  }

  const result = data as { success?: boolean; url?: string; error?: string } | null;
  if (!result?.success || !result.url) {
    throw new Error(result?.error || "Upload failed");
  }

  return result.url;
}

function getInternalMediaKeyFromUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    const markerIndex = parsed.pathname.indexOf(INTERNAL_MEDIA_SEGMENT);
    if (markerIndex < 0) return null;
    const encodedKey = parsed.pathname.slice(markerIndex + INTERNAL_MEDIA_SEGMENT.length);
    if (!encodedKey) return null;
    return encodedKey
      .split("/")
      .filter(Boolean)
      .map((segment) => decodeURIComponent(segment))
      .join("/");
  } catch {
    return null;
  }
}

/**
 * Upload a file to Cloudflare R2 via the edge function.
 * Returns the public URL of the uploaded file.
 * Images are automatically compressed to WebP (1920px max, 0.80 quality, 1MB limit) unless compress is false.
 */
export async function uploadToR2(
  file: File | Blob,
  folder: string,
  fileName?: string,
  options?: { compress?: boolean; maxDim?: number }
): Promise<string> {
  const shouldCompress = (options?.compress ?? true) && isImageFile(file);

  let uploadFile: File | Blob = file;
  let finalName = fileName || `${crypto.randomUUID()}.${getExtension(file)}`;

  if (shouldCompress) {
    try {
      const img = await loadImage(file);
      uploadFile = await renderToBlob(img, options?.maxDim ?? 1920, 0.80, 1_000_000);
      finalName = finalName.replace(/\.[^.]+$/, ".webp");
    } catch {
      // Compression failed, upload original
      uploadFile = file;
    }
  }

  const formData = new FormData();
  formData.append("file", uploadFile, finalName);
  formData.append("folder", folder);
  formData.append("fileName", finalName);
  return invokeMediaUpload((await shouldUseInternalMedia(folder)) ? "vps-media" : "r2-upload", formData);
}

function getExtension(file: File | Blob): string {
  if (file instanceof File) {
    return file.name.split(".").pop() || "bin";
  }
  const mimeMap: Record<string, string> = {
    "application/pdf": "pdf",
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/webp": "webp",
  };
  return mimeMap[file.type] || "bin";
}

/**
 * Load image file into an HTMLImageElement (once).
 */
function loadImage(file: File | Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Failed to load image")); };
    img.src = url;
  });
}

/**
 * Render image to canvas at given max dimension and return blob.
 */
async function renderToBlob(img: HTMLImageElement, maxDim: number, quality: number, maxBytes?: number): Promise<Blob> {
  const renderAtSize = (w: number, h: number, q: number): Promise<Blob> => {
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(img, 0, 0, w, h);
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("Canvas toBlob failed"))),
        "image/webp",
        q
      );
    });
  };

  let { width, height } = img;
  if (width > maxDim || height > maxDim) {
    const ratio = Math.min(maxDim / width, maxDim / height);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  let currentQuality = quality;
  const MAX_RETRIES = 6;
  const MIN_QUALITY = 0.20;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    const blob = await renderAtSize(width, height, currentQuality);
    if (!maxBytes || blob.size <= maxBytes) {
      return blob;
    }
    currentQuality = Math.max(currentQuality - 0.10, MIN_QUALITY);
    if (currentQuality <= MIN_QUALITY && attempt < MAX_RETRIES) continue;
  }

  // Final fallback: shrink dimensions to 75% and retry at 0.60
  const shrunkW = Math.round(width * 0.75);
  const shrunkH = Math.round(height * 0.75);
  return renderAtSize(shrunkW, shrunkH, 0.60);
}

/**
 * Generate a tiny ~10px wide base64 blur placeholder from an image.
 */
function generateBlurDataUrl(img: HTMLImageElement): string {
  const maxDim = 10;
  let { width, height } = img;
  const ratio = Math.min(maxDim / width, maxDim / height);
  width = Math.round(width * ratio);
  height = Math.round(height * ratio);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL("image/webp", 0.1);
}

/**
 * Delete a file from R2 (or VPS internal-media) by its public URL.
 * Returns `true` when the file was deleted OR there is nothing to delete (no URL,
 * or an unrecognized/non-managed URL); returns `false` when a delete was attempted
 * but failed. Never throws. Callers that clear a DB reference after deletion should
 * gate the clear on a `true` result — clearing the reference when the delete failed
 * strands the file as a permanent orphan (no row left to retry from).
 */
export async function deleteR2FileByUrl(url: string | null | undefined): Promise<boolean> {
  if (!url) return true;
  if (url.includes(R2_DOMAIN)) {
    const key = url.replace(`https://${R2_DOMAIN}/`, "");
    if (!key || key === url) return true;

    try {
      await invokeR2Manage({ action: "delete", key });
      return true;
    } catch (e) {
      console.warn("Failed to delete R2 file:", key, e);
      return false;
    }
  }

  const internalKey = getInternalMediaKeyFromUrl(url);
  if (!internalKey) return true;

  try {
    await ensureUploadSession();
    const { error } = await supabase.functions.invoke("vps-media", {
      body: { action: "delete", key: internalKey },
    });
    if (error) {
      throw new Error(getInvokeErrorMessage(error, "Delete failed"));
    }
    return true;
  } catch (e) {
    console.warn("Failed to delete VPS media file:", internalKey, e);
    return false;
  }
}

/**
 * Upload an image plus derived display sizes under a derivable naming
 * convention: <name>.webp (1920 main) + <name>-thumb.webp (480) +
 * <name>-med.webp (1080). Only the main URL is returned/stored —
 * displayR2Url(url, w) derives the sibling names at render time.
 * Used for b2c-products so the storefront never downloads full-size
 * originals (backfill for pre-existing images:
 * scripts/backfill-b2c-image-sizes.mjs).
 */
export async function uploadImageWithDerivedSizes(
  file: File | Blob,
  folder: string,
  fileName?: string
): Promise<string> {
  const baseName = (fileName || crypto.randomUUID()).replace(/\.[^.]+$/, "");
  const img = await loadImage(file);

  const [mainBlob, thumbBlob, medBlob] = await Promise.all([
    renderToBlob(img, 1920, 0.80, 1_000_000),
    renderToBlob(img, 480, 0.65, 200_000),
    renderToBlob(img, 1080, 0.72, 500_000),
  ]);

  const uploadOne = async (blob: Blob, suffix: string): Promise<string> => {
    const name = `${baseName}${suffix}.webp`;
    const formData = new FormData();
    formData.append("file", blob, name);
    formData.append("folder", folder);
    formData.append("fileName", name);
    return invokeMediaUpload((await shouldUseInternalMedia(folder)) ? "vps-media" : "r2-upload", formData);
  };

  const results = await Promise.allSettled([
    uploadOne(mainBlob, ""),
    uploadOne(thumbBlob, "-thumb"),
    uploadOne(medBlob, "-med"),
  ]);

  const failed = results.find((r) => r.status === "rejected");
  if (failed) {
    const succeeded = results
      .filter((r): r is PromiseFulfilledResult<string> => r.status === "fulfilled")
      .map((r) => r.value);
    await Promise.allSettled(succeeded.map((u) => deleteR2FileByUrl(u)));
    throw new Error((failed as PromiseRejectedResult).reason?.message || "Image upload failed");
  }

  return (results[0] as PromiseFulfilledResult<string>).value;
}

export interface ImageVariantResult {
  url: string;
  thumbUrl: string;
  medUrl: string;
  blurDataUrl: string;
}

/**
 * Upload an image to R2 in 3 responsive variants (thumb/med/large) + generate blur placeholder.
 * Returns URLs for all variants and a base64 blur data URL.
 */
export async function uploadImageToR2WithVariants(
  file: File | Blob,
  folder: string,
  fileName?: string
): Promise<ImageVariantResult> {
  const baseName = (fileName || `${crypto.randomUUID()}`).replace(/\.[^.]+$/, "");

  const img = await loadImage(file);
  const blurDataUrl = generateBlurDataUrl(img);

  // Generate all 3 blobs from the same loaded image
  const [thumbBlob, medBlob, largeBlob] = await Promise.all([
    renderToBlob(img, 480, 0.65, 200_000),
    renderToBlob(img, 1020, 0.72, 500_000),
    renderToBlob(img, 1920, 0.80, 1_000_000),
  ]);

  // Upload all 3 variants in parallel
  const uploadVariant = async (blob: Blob, suffix: string): Promise<string> => {
    const variantName = `${baseName}${suffix}.webp`;
    const formData = new FormData();
    formData.append("file", blob, variantName);
    formData.append("folder", folder);
    formData.append("fileName", variantName);
    return invokeMediaUpload((await shouldUseInternalMedia(folder)) ? "vps-media" : "r2-upload", formData);
  };

  const results = await Promise.allSettled([
    uploadVariant(thumbBlob, "-thumb"),
    uploadVariant(medBlob, "-med"),
    uploadVariant(largeBlob, ""),
  ]);

  // If any variant failed, clean up the ones that succeeded and throw
  const failed = results.find(r => r.status === "rejected");
  if (failed) {
    const succeeded = results
      .filter((r): r is PromiseFulfilledResult<string> => r.status === "fulfilled")
      .map(r => r.value);
    await Promise.allSettled(succeeded.map(u => deleteR2FileByUrl(u)));
    throw new Error((failed as PromiseRejectedResult).reason?.message || "Variant upload failed");
  }

  const [thumbUrl, medUrl, url] = results.map(
    r => (r as PromiseFulfilledResult<string>).value
  );

  return { url, thumbUrl, medUrl, blurDataUrl };
}
